// Native import() failures carry a per-browser TypeError message. They have no stack frames, and Safari's messages (a
// failed request, or HTML for a missing file) name no URL.
export const chunkLoadFailureMessage =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|is not a valid JavaScript MIME type/;

/** The TypeError a browser rejects a failed import() with. */
export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'TypeError' && chunkLoadFailureMessage.test(error.message);

const retryDelayMs = 1000;

/**
 * Tries a chunk that failed to load once more after a second. Browsers that cache a failed module fetch (Safari 26.2+,
 * Chrome before 156, Firefox before 155) fail the retry at once, which leaves recovery to a reload. Unlike
 * retryPromise, other errors aren't retried: an error thrown while running a module comes back on every try.
 */
export const retryChunkLoad = <T>(load: () => Promise<T>): Promise<T> =>
  load().catch(async (error: unknown) => {
    if (!isChunkLoadError(error)) throw error;
    await new Promise((resolve) => {
      setTimeout(resolve, retryDelayMs);
    });
    return load();
  });

const reloadedAtKey = 'chunkLoadReloadedAt';
// A chunk that still fails within this long of a reload gets the error page, so a lasting failure can't loop.
const reloadWindowMs = 10 * 60 * 1000;
// On a network too slow to send the error report, the reload doesn't wait for it longer than this.
const reportWaitMs = 3000;

/**
 * Whether a reload may fetch a failed chunk again: the browser is online and sessionStorage holds no reload for one in
 * the last 10 minutes. A new tab that copies this tab's sessionStorage (tabAuthExchange) copies the record too.
 */
export const mayReloadForChunkLoadError = (): boolean => {
  if (navigator.onLine === false) return false;
  try {
    const sinceReload = Date.now() - Number(window.sessionStorage.getItem(reloadedAtKey));
    return !(sinceReload >= 0 && sinceReload < reloadWindowMs);
  } catch {
    return false;
  }
};

/**
 * Reloads the page so the browser fetches the failed chunk again. The reload is recorded first; when that fails,
 * nothing reloads and this returns false. `report` sends the error report and calls `onReported` once Bugsnag is done
 * with it; without that call, as while Bugsnag is disabled, the reload waits 3 s.
 */
export const reloadForChunkLoadError = (report: (onReported: () => void) => void): boolean => {
  try {
    window.sessionStorage.setItem(reloadedAtKey, String(Date.now()));
  } catch {
    return false;
  }
  let reloaded = false;
  const reload = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };
  report(reload);
  setTimeout(reload, reportWaitMs);
  return true;
};
